import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { ErrorResponse } from "../../../custom-fetch";
import {
  EModalId,
  EModalSize,
  useModalContext,
} from "../../ui/Modal/modal-context";
import ReservationCancelModal from "./ReservationCancelModal";
import IReservation from "../../../shared/IReservation";
import { useReservationService } from "../../../services/reservations-service";
import { QueryKey } from "../../../services/query-keys";

export const useReservationCancelModal = () => {
  const { addModal, closeModal } = useModalContext();
  const queryClient = useQueryClient();

  const closeModalHandler = useCallback(
    () => closeModal(EModalId.RESERVATION_CANCEL),
    [closeModal]
  );

  const { cancelMutate } = useCancel();

  const handler = (reservation: IReservation): Promise<boolean> => {
    let title = "Cancel reservation";
    return new Promise((resolve) => {
      addModal({
        id: EModalId.RESERVATION_CANCEL,
        isOpen: true,
        size: EModalSize.SMALL,
        title,
        onModalClose: () => resolve(false),
        content: (
          <ReservationCancelModal
            onAccept={async (data) => {
              try {
                await cancelMutate(data);
                queryClient.invalidateQueries({ queryKey: [QueryKey.reservations] });
                resolve(true);
                closeModalHandler();
                return null;
              } catch (error) {
                console.error(error);
                return error as ErrorResponse;
              }
            }}
            onCancel={() => {
              closeModalHandler();
              resolve(false);
            }}
            data={reservation}
          />
        ),
      });
    });
  };

  return handler;
};

const useCancel = () => {
  const { cancelReservation } = useReservationService();

  const {
    mutateAsync: cancelMutate,
    isPending,
    isError,
    error,
  } = useMutation<IReservation | undefined, ErrorResponse, IReservation>({
    mutationFn: (data) => cancelReservation(data),
  });

  return { cancelMutate, isPending, isError, error };
};
